import React, { useState, useEffect } from 'react';
import { 
  Flag, 
  Users, 
  Building2, 
  CheckCircle2, 
  XCircle, 
  Search, 
  ToggleLeft, 
  ToggleRight,
  Loader2,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface FeatureFlag {
  featureName: string;
  displayName: string;
  description: string;
  isGloballyEnabled: boolean;
  enabledAccountsCount: number;
  enabledUsersCount: number;
  disabledUsersCount: number;
}

interface Account {
  accountId: string;
  accountName: string;
}

interface UserAccess {
  userId: string;
  userName: string;
  userEmail: string;
  accessType: 'granted' | 'denied';
}

export const FeatureFlags: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [featureFlag, setFeatureFlag] = useState<FeatureFlag | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<UserAccess[]>([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [allAccounts, setAllAccounts] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);

  const isSystemAdmin = user?.role === 'System Administrator';

  useEffect(() => {
    if (isSystemAdmin) {
      fetchFeatureFlag();
      fetchAllAccounts();
      fetchAllUsers();
    }
  }, [isSystemAdmin]);

  const fetchFeatureFlag = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Get feature flag status
      const statusResponse = await fetch(`${apiUrl}/api/feature-flags/buddyai`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!statusResponse.ok) {
        throw new Error('Failed to fetch feature flag');
      }
      
      const statusData = await statusResponse.json();
      const flag = statusData.data;
      
      // Get status summary
      const summaryResponse = await fetch(`${apiUrl}/api/feature-flags/buddyai`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const summaryData = await summaryResponse.json();
      
      setFeatureFlag({
        featureName: flag.featureName,
        displayName: flag.displayName,
        description: flag.description,
        isGloballyEnabled: flag.isGloballyEnabled,
        enabledAccountsCount: flag.enabledForAccounts?.length || 0,
        enabledUsersCount: flag.enabledForUsers?.length || 0,
        disabledUsersCount: flag.disabledForUsers?.length || 0,
      });
      
      // Fetch enabled accounts
      const accountsResponse = await fetch(`${apiUrl}/api/feature-flags/buddyai/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        setAccounts(accountsData.data || []);
      }
      
      // Fetch users with explicit access
      const usersResponse = await fetch(`${apiUrl}/api/feature-flags/buddyai/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.data || []);
      }
      
    } catch (err) {
      console.error('Failed to fetch feature flag:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllAccounts(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const toggleGlobalEnabled = async () => {
    if (!featureFlag) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/feature-flags/buddyai/global`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          enabled: !featureFlag.isGloballyEnabled
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update global setting');
      }
      
      setSuccessMessage(`Feature ${!featureFlag.isGloballyEnabled ? 'enabled' : 'disabled'} globally`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      await fetchFeatureFlag();
    } catch (err) {
      console.error('Failed to toggle global enabled:', err);
      setError(err instanceof Error ? err.message : 'Failed to update global setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleAccountAccess = async (accountId: string, currentlyEnabled: boolean) => {
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const url = `${apiUrl}/api/feature-flags/buddyai/accounts/${accountId}`;
      
      const response = await fetch(url, {
        method: currentlyEnabled ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${currentlyEnabled ? 'disable' : 'enable'} account access`);
      }
      
      setSuccessMessage(`Account ${currentlyEnabled ? 'disabled' : 'enabled'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      await fetchFeatureFlag();
    } catch (err) {
      console.error('Failed to toggle account access:', err);
      setError(err instanceof Error ? err.message : 'Failed to update account access');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserAccess = async (userId: string, accessType: 'granted' | 'denied') => {
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const url = `${apiUrl}/api/feature-flags/buddyai/users/${userId}`;
      
      // If currently granted, revoke. If denied, grant.
      const response = await fetch(url, {
        method: accessType === 'granted' ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${accessType === 'granted' ? 'revoke' : 'grant'} user access`);
      }
      
      setSuccessMessage(`User access ${accessType === 'granted' ? 'revoked' : 'granted'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      
      await fetchFeatureFlag();
    } catch (err) {
      console.error('Failed to toggle user access:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user access');
    } finally {
      setSaving(false);
    }
  };

  const filteredAccounts = allAccounts.filter(acc => 
    acc.companyName?.toLowerCase().includes(accountSearch.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u => 
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (!isSystemAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-warning-800 dark:text-warning-200">
            Access denied. System Administrator privileges required.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!featureFlag) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-error-800 dark:text-error-200">
            Feature flag not found. Please run the migration script.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Feature Flags
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage feature access for Buddy AI Chat
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-success-600 dark:text-success-400" />
          <p className="text-success-800 dark:text-success-200">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
          <p className="text-error-800 dark:text-error-200">{error}</p>
        </div>
      )}

      {/* Feature Overview Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {featureFlag.displayName}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {featureFlag.description}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${
              featureFlag.isGloballyEnabled 
                ? 'text-success-600 dark:text-success-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {featureFlag.isGloballyEnabled ? 'Globally Enabled' : 'Coming Soon'}
            </span>
            <button
              onClick={toggleGlobalEnabled}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                featureFlag.isGloballyEnabled 
                  ? 'bg-primary-500' 
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  featureFlag.isGloballyEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {featureFlag.enabledAccountsCount}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Accounts Enabled</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">
              {featureFlag.enabledUsersCount}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Users Granted</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-error-600 dark:text-error-400">
              {featureFlag.disabledUsersCount}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Users Denied</div>
          </div>
        </div>
      </div>

      {/* Account Management */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Account Management
        </h3>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Accounts Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Account Name</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => {
                const isEnabled = accounts.some(acc => acc.accountId === account._id);
                return (
                  <tr key={account._id} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100">
                      {account.companyName}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {isEnabled ? (
                        <span className="inline-flex items-center gap-1 text-success-600 dark:text-success-400">
                          <CheckCircle2 className="w-4 h-4" />
                          Enabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <XCircle className="w-4 h-4" />
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => toggleAccountAccess(account._id, isEnabled)}
                        disabled={saving}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          isEnabled
                            ? 'bg-error-100 text-error-700 hover:bg-error-200 dark:bg-gray-700 dark:border dark:border-error-800/50 dark:text-error-400'
                            : 'bg-success-100 text-success-700 hover:bg-success-200 dark:bg-gray-700 dark:border dark:border-success-800/50 dark:text-success-400'
                        }`}
                      >
                        {isEnabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Management */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          User Management
        </h3>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Email</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Access</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const userAccess = users.find(u => u.userId === user._id);
                const accessType = userAccess?.accessType;
                
                return (
                  <tr key={user._id} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {user.email}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {accessType === 'granted' ? (
                        <span className="inline-flex items-center gap-1 text-success-600 dark:text-success-400">
                          <CheckCircle2 className="w-4 h-4" />
                          Granted
                        </span>
                      ) : accessType === 'denied' ? (
                        <span className="inline-flex items-center gap-1 text-error-600 dark:text-error-400">
                          <XCircle className="w-4 h-4" />
                          Denied
                        </span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400 text-sm">Inherited</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {accessType === 'granted' ? (
                        <button
                          onClick={() => toggleUserAccess(user._id, 'granted')}
                          disabled={saving}
                          className="px-3 py-1 rounded text-sm font-medium bg-error-100 text-error-700 hover:bg-error-200 dark:bg-gray-700 dark:border dark:border-error-800/50 dark:text-error-400 transition-colors"
                        >
                          Revoke
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleUserAccess(user._id, 'denied')}
                          disabled={saving}
                          className="px-3 py-1 rounded text-sm font-medium bg-success-100 text-success-700 hover:bg-success-200 dark:bg-gray-700 dark:border dark:border-success-800/50 dark:text-success-400 transition-colors"
                        >
                          Grant
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
